import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categoryGroups } from "@/mocks/categories";

const mainLinks = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/series" },
  { label: "短视频", to: "/category/short" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap px-3 py-2 text-sm transition-colors ${
      isActive
        ? "text-primary-400"
        : "text-foreground-700 hover:text-foreground-950"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled || menuOpen
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur-xl"
          : "border-b border-transparent bg-gradient-to-b from-background-50/90 to-background-50/40 backdrop-blur-md"
      }`}
    >
      <div className="page-shell flex h-16 w-full items-center justify-between gap-4 md:h-[72px]">
        <Link to="/" className="group flex items-center gap-2.5" aria-label="逼特逼视频首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 transition-transform group-hover:scale-105">
            <i className="ri-play-fill text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[17px] font-semibold tracking-tight text-foreground-950">
              逼特逼视频
            </span>
            <span className="mt-1 font-label text-[10px] uppercase tracking-[0.22em] text-foreground-500">
              Better Video
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {mainLinks.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === "/"} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-400 lg:flex"
          >
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影、剧集、短视频"
              aria-label="站内搜索"
              className="w-[172px] bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded bg-primary-500 px-2.5 py-1 font-label text-xs text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-400 md:hidden"
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <button
            type="button"
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-400 md:hidden"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>

          <Link
            to="/about"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            反馈建议
          </Link>
        </div>
      </div>

      {menuOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 md:hidden">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
          >
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索电影、剧集、短视频"
              aria-label="站内搜索"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
            <button
              type="submit"
              aria-label="提交搜索"
              className="flex h-7 w-7 items-center justify-center rounded bg-primary-500 text-background-50"
            >
              <i className="ri-arrow-right-line text-base" />
            </button>
          </form>

          <div className="mt-4 flex flex-col">
            {mainLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between border-b border-background-200/70 py-3 text-[15px] text-foreground-800"
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-foreground-500" />
              </NavLink>
            ))}
          </div>

          <p className="mb-2 mt-5 font-label text-[11px] uppercase tracking-[0.18em] text-foreground-500">
            内容分类
          </p>
          <div className="flex flex-wrap gap-2">
            {categoryGroups.map((group) => (
              <Link
                key={group.type}
                to={`/category/${group.type}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-700"
              >
                <i className={`${group.icon} text-sm text-primary-400`} />
                {group.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}